import { FC } from "react";

import Stack from "@mui/material/Stack";
import {
  ChargeDayCustomizableInterval,
  formatDateToMedDate,
  getDateFormatFromLocale,
  getLastChargeDateBefore,
  getLatestScheduledEndDate,
  getScheduledEndDate,
  IsoDate,
  SubscriptionRecurrenceInterval,
} from "@simplyk/common";
import { DateTime } from "luxon";
import { useForm, useWatch } from "react-hook-form";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDatePicker } from "@/dashboard-design-system/DashboardDatePicker";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Date as DateIcon } from "@/icons/outlined";

const FREQUENCY_KEYS = {
  [SubscriptionRecurrenceInterval.Weekly]: "weekly",
  [SubscriptionRecurrenceInterval.BiWeekly]: "biWeekly",
  [SubscriptionRecurrenceInterval.Monthly]: "monthly",
} as const;

interface ScheduleEndDateFormData {
  endDate: IsoDate;
}

interface ScheduleEndDateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (endDate: IsoDate) => Promise<boolean>;
  isLoading: boolean;
  nextChargeDate: Date | string;
  recurrenceInterval: ChargeDayCustomizableInterval;
  billingAnchorDayOfMonth?: number | null;
  formattedAmount: string;
  organizationName: string;
  timezone?: string | null;
  defaultEndDate?: string;
  inline?: boolean;
}

export const ScheduleEndDateDialog: FC<ScheduleEndDateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  nextChargeDate,
  recurrenceInterval,
  billingAnchorDayOfMonth,
  formattedAmount,
  organizationName,
  timezone,
  defaultEndDate = "",
  inline,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const { control, reset } = useForm<ScheduleEndDateFormData>({ defaultValues: { endDate: defaultEndDate } });
  const endDate = useWatch({ control, name: "endDate" });

  const endTimezone = timezone || "utc";
  const today = DateTime.now().setZone(endTimezone);
  const isInTheFuture = endDate ? endDate > ((today.isValid ? today : DateTime.local()).toISODate() ?? "") : false;
  const lastChargeDate =
    endDate && isInTheFuture
      ? getLastChargeDateBefore({
          nextChargeDate,
          recurrenceInterval,
          endDate: getScheduledEndDate({ endDate, timezone: endTimezone }),
          billingAnchorDayOfMonth,
        })
      : null;

  const handleClose = () => {
    reset({ endDate: defaultEndDate });
    onClose();
  };

  const handleConfirm = async () => {
    if (!endDate || !isInTheFuture) {
      return;
    }
    const success = await onConfirm(endDate);
    if (success) {
      handleClose();
    }
  };

  const renderHelper = () => {
    if (!endDate) {
      return null;
    }
    if (!isInTheFuture) {
      return (
        <DashboardTypography variant="body2" vibe="text-danger-moderate" data-test="schedule-end-date-error">
          {t("dashboard", "scheduleEndDateDialog.endDateInThePast")}
        </DashboardTypography>
      );
    }
    return (
      <DashboardTypography variant="body2" vibe="text-brand-moderate" data-test="schedule-end-date-last-renewal">
        {lastChargeDate ? (
          <Trans
            space="dashboard"
            i18nKey="scheduleEndDateDialog.lastRenewal"
            values={{ date: formatDateToMedDate(lastChargeDate, isoLocale, endTimezone) }}
            components={{
              bold: <DashboardTypography component="span" variant="body2" sx={{ fontWeight: 700 }} />,
            }}
          />
        ) : (
          t("dashboard", "scheduleEndDateDialog.noMoreRenewal")
        )}
      </DashboardTypography>
    );
  };

  return (
    <DashboardDialog open={open} onClose={handleClose} maxWidth="xs" vibe="brand" inline={inline}>
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<DateIcon />} title={t("dashboard", "scheduleEndDateDialog.title")} />,
          content: (
            <DashboardDialogContent>
              <Stack gap={1.5}>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", `scheduleEndDateDialog.description.${FREQUENCY_KEYS[recurrenceInterval]}`, {
                    amount: formattedAmount,
                    organizationName,
                  })}
                </DashboardTypography>
                <DashboardDatePicker
                  control={control}
                  name="endDate"
                  size="small"
                  minDate={today.plus({ days: 1 }).startOf("day")}
                  maxDate={DateTime.fromISO(getLatestScheduledEndDate())}
                  format={getDateFormatFromLocale(isoLocale)}
                  placeholder={getDateFormatFromLocale(isoLocale).toUpperCase()}
                  noAsterisk
                  data-test="schedule-end-date-input"
                />
                {renderHelper()}
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton
                onClick={handleConfirm}
                isLoading={isLoading}
                disabled={!isInTheFuture}
                data-test="schedule-end-date-confirm"
              >
                {t("dashboard", "scheduleEndDateDialog.confirm")}
              </DashboardButton>
              <DashboardButton onClick={handleClose}>{t("dashboard", "scheduleEndDateDialog.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
